import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAnalytics } from "./useAnalytics";
import { Sentry } from "../instrument";

/**
 * Navigations here commit synchronously (flushSync), so anything still
 * uncommitted after this window is a dead router, not a slow device.
 */
const COMMIT_TIMEOUT_MS = 1_000;

/**
 * useNavigate hardened against the post-restore dead-navigation bug: after iOS
 * jetsams the WebView and Capacitor restore-reloads it, router navigations have
 * been observed to silently stop committing while the rest of the app stays
 * interactive (v1.2 build 23, screen-recorded 2026-08-17). Three defences,
 * covering every suspected mechanism:
 *
 * 1. flushSync — route state commits on the sync lane instead of the default
 *    startTransition lane, the one lane observed dead in restored WebViews.
 * 2. The rejection of react-router's internal async navigate is caught —
 *    useNavigate otherwise swallows it as an unhandled rejection with no UI
 *    feedback.
 * 3. A watchdog verifies React committed a new location (location.key changes
 *    on every committed navigation, even same-path pushes) and otherwise falls
 *    back to a full document navigation — heavyweight, but the only escape that
 *    reliably revives a wedged WebView — reporting to PostHog/Sentry so we
 *    learn which mechanism actually fires in production.
 */
export const useSafeNavigate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { capture } = useAnalytics();

  const committedKeyRef = useRef(location.key);
  useEffect(() => {
    committedKeyRef.current = location.key;
  }, [location.key]);

  return useCallback(
    (to: string | number, options?: { replace?: boolean }) => {
      const replace = options?.replace ?? false;
      const keyAtCall = committedKeyRef.current;
      let recovered = false;

      const recover = (reason: string, error?: unknown) => {
        if (recovered || committedKeyRef.current !== keyAtCall) return;
        recovered = true;
        capture("nav_watchdog_recovery", { reason, target: String(to) });
        Sentry.captureException(
          error instanceof Error
            ? error
            : new Error(`safeNavigate ${reason}: ${String(to)}`),
          { tags: { nav_recovery: reason } },
        );
        if (typeof to === "number") {
          // history.go already moved the URL if it worked at all, so reloading
          // boots the document at the right entry either way.
          window.location.reload();
        } else if (replace) {
          window.location.replace(to);
        } else {
          window.location.assign(to);
        }
      };

      try {
        // Delta navigation resolves through an async popstate, which accepts
        // no options — the watchdog is its only defence.
        const result =
          typeof to === "number"
            ? navigate(to)
            : navigate(to, { flushSync: true, replace });
        Promise.resolve(result).catch((error: unknown) =>
          recover("navigate_rejected", error),
        );
      } catch (error) {
        recover("navigate_threw", error);
        return;
      }
      window.setTimeout(() => recover("commit_timeout"), COMMIT_TIMEOUT_MS);
    },
    [navigate, capture],
  );
};
